import { log } from '@js/utils/logger'
import { getAuthUserCart } from '@js/api/cart/getAuthUserCart'
import { removeCouponFromCart } from '@js/api/cart/removeCouponFromCart'
import { registerAndUpsertItemToCart } from '@js/api/checkout/registerAndUpsertItemToCart'

import { Cart } from '@js/api/types/cart/Cart'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

export default function useCart() {
  type AddItemToCartResult =
    | { status: 'success'; data: CustomerRegistration }
    | { status: 'error'; data: string }

  const ALLOWED_MAGENTO_ERRORS = [
    'You are already enrolled in this class.',
  ]

  const setCartInStorage = (cart: Cart): void => {
    localStorage.setItem('miniCart', JSON.stringify(cart))
  }

  const getCartFromMagento = async (): Promise<void> => {
    try {
      const cart = await getAuthUserCart()

      if (cart) {
        setCartInStorage(cart)
      }
    } catch (error) {
      log({
        file: 'src/wcei/js/composables/useCart.ts',
        message: 'Error retrieving authenticated user cart from Magento',
      })
    }
  }

  const getCartFromStorage = (): Cart | null => {
    const miniCart = localStorage.getItem('miniCart')

    return miniCart ? JSON.parse(miniCart) as Cart : null
  }

  const clearCart = (): void => {
    localStorage.removeItem('miniCart')
  }

  const addItemToCart = async (sku: string, courseOption: string, craftEntryId: number, registration?: CustomerRegistration): Promise<AddItemToCartResult> =>  {
    try {
      // The user's cart must be retrieved from Magento prior to adding an item to it
      // because the cart ID changes each time the user completes the checkout process.
      // The cart can't be cleared after successful checkout because the data is used
      // on the confirmation page. It also can't be reset upon leaving the confirmation
      // page because the Vue component's lifecycle hooks get terminated due to navigation.
      await getCartFromMagento()

      let cart = getCartFromStorage()

      // ensures a coupon applied to a prior, unfinished registration is only valid for that
      // registration's items. remove it so it isn't silently carried over (and applied)
      // unchecked to a new registration.
      if (cart?.id && cart.coupon_details?.code) {
        cart = await removeCouponFromCart({ cartId: cart.id })

        if (cart) {
          setCartInStorage(cart)
        }
      }

      const result = await registerAndUpsertItemToCart({
        qty: 1,
        sku: sku,
        registrationId: registration?.registration_id ?? null,
        cartId: cart?.id ?? '',
        courseOption: courseOption,
        craftEntryId: craftEntryId,
        personal: {
          upsert_address: false,
          country: registration?.personal.country ?? '',
          address: registration?.personal.address ?? '',
          address_two: registration?.personal.address_two ?? '',
          city: registration?.personal.city ?? '',
          state: registration?.personal.state ?? '',
          postcode: registration?.personal.postcode ?? '',
          phone: registration?.personal.phone ?? '',
          secondary_phone: registration?.personal.secondary_phone ?? '',
        },
        professional: {
          licenses: registration?.professional.licenses.map(license => license.license_id) ?? [],
          primary_employment: registration?.professional.primary_employment ?? '',
          employer_name: registration?.professional.employer_name ?? '',
          employer_city: registration?.professional.employer_city ?? '',
          employer_state: registration?.professional.employer_state ?? '',
          management_level: registration?.professional.management_level ?? '',
          hear_about: registration?.professional.hear_about ?? '',
          certifications: registration?.professional.certifications?.split(',') || [],
        },
      })

      if (!result) {
        return { status: 'error', data: 'Failed to add item to cart' }
      }

      return { 
        status: 'success', 
        data: result,
      }
    } catch (error) {
      const message = error ? String(error) : ''

      const allowedMessage = ALLOWED_MAGENTO_ERRORS.find(
        allowed => message.includes(allowed)
      )

      log({
        message: message || 'Error adding item to cart',
        file: 'src/wcei/js/composables/useCart.ts',
      })

      return {
        status: 'error',
        data: allowedMessage ?? 'Error adding item to cart',
      }
    }
  }

  return {
    clearCart,
    addItemToCart,
    setCartInStorage,
    getCartFromMagento,
    getCartFromStorage,
  }
}
